import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit2, Check, X, Plus, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { FurnitureList } from '../../components/os/FurnitureList';

const STAGES = [
  'client_intake', 'design_proposal', 'design_phase_a', 'design_phase_b',
  'design_phase_c', 'installation_proposal', 'procurement', 'installation', 'completed'
];
const STAGE_LABELS = {
  client_intake: 'Intake', design_proposal: 'Propuesta Diseño',
  design_phase_a: 'Diseño A', design_phase_b: 'Diseño B', design_phase_c: 'Diseño C',
  installation_proposal: 'Propuesta Montaje', procurement: 'Procurement',
  installation: 'Instalación', completed: 'Entregado',
};
const STAGE_COLORS = {
  client_intake: '#C9B8A8', design_proposal: '#C9A66B', design_phase_a: '#D4A574',
  design_phase_b: '#D4A574', design_phase_c: '#D4A574',
  installation_proposal: '#C9A66B', procurement: '#8B9F8D',
  installation: '#8B9F8D', completed: '#6B8F6B',
};
const TASK_STATUSES = ['pending', 'in_progress', 'review', 'completed'];
const STATUS_LABELS = { pending: 'Pendiente', in_progress: 'En Progreso', review: 'Revisión', completed: 'Completada' };

export default function OSProjectDetail() {
  const { id } = useParams();
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editStage, setEditStage] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', priority: 'medium', category: '' });
  const [savingTask, setSavingTask] = useState(false);

  const load = async () => {
    try {
      const [pRes, tRes] = await Promise.all([
        authFetch(`/api/projects/${id}`),
        authFetch(`/api/tasks/?project_id=${id}`),
      ]);
      if (!pRes.ok) throw new Error();
      setProject(await pRes.json());
      setTasks(await tRes.json());
    } catch { navigate('/os/projects'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [id]);

  const advanceStage = async (newStage) => {
    try {
      const res = await authFetch(`/api/projects/${id}`, { method: 'PATCH', body: JSON.stringify({ stage: newStage }) });
      if (!res.ok) throw new Error();
      setProject(await res.json());
      setEditStage(false);
      toast.success('Etapa actualizada');
    } catch { toast.error('Error al actualizar'); }
  };

  const createTask = async (e) => {
    e.preventDefault();
    setSavingTask(true);
    try {
      const res = await authFetch('/api/tasks/', { method: 'POST', body: JSON.stringify({ ...taskForm, project_id: id }) });
      if (!res.ok) throw new Error();
      toast.success('Tarea creada');
      setShowTaskModal(false);
      setTaskForm({ title: '', description: '', priority: 'medium', category: '' });
      load();
    } catch { toast.error('Error al crear tarea'); }
    setSavingTask(false);
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      await authFetch(`/api/tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      load();
    } catch { toast.error('Error'); }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#C9A66B', borderTopColor: 'transparent' }} /></div>;
  if (!project) return null;

  const stageIdx = STAGES.indexOf(project.stage);
  const canEdit = user?.role === 'admin' || user?.role === 'designer';

  return (
    <div data-testid="project-detail" className="space-y-8">
      {/* Back */}
      <button onClick={() => navigate('/os/projects')} className="flex items-center gap-2 text-sm font-light transition-colors" style={{ color: '#C9B8A8' }}
        onMouseEnter={e => e.currentTarget.style.color = '#C9A66B'} onMouseLeave={e => e.currentTarget.style.color = '#C9B8A8'}>
        <ArrowLeft size={16} /> Proyectos
      </button>

      {/* Project Header */}
      <div className="rounded-xl border p-6" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-light tracking-tight" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>{project.name}</h1>
            <p className="text-sm mt-1" style={{ color: '#C9B8A8' }}>Cliente: {project.client_name}</p>
            {project.address && <p className="text-xs mt-1" style={{ color: '#C9B8A8' }}>{project.address}</p>}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs px-3 py-1.5 rounded-full font-medium"
              style={{ backgroundColor: (STAGE_COLORS[project.stage] || '#C9B8A8') + '20', color: STAGE_COLORS[project.stage] }}>
              {STAGE_LABELS[project.stage]}
            </span>
          </div>
        </div>

        {/* Pipeline */}
        <div className="overflow-x-auto pb-2">
          <div className="flex items-center gap-1 min-w-max">
            {STAGES.map((s, i) => {
              const isActive = i === stageIdx;
              const isDone = i < stageIdx;
              const color = isDone ? '#8B9F8D' : isActive ? (STAGE_COLORS[s] || '#C9A66B') : '#E8E3D8';
              return (
                <React.Fragment key={s}>
                  <button
                    data-testid={`stage-${s}`}
                    disabled={!canEdit || (!editStage && !isActive)}
                    onClick={() => editStage && canEdit && advanceStage(s)}
                    className={`text-[10px] px-3 py-1.5 rounded-full transition-all whitespace-nowrap ${editStage && canEdit ? 'cursor-pointer hover:opacity-80' : ''}`}
                    style={{
                      backgroundColor: isDone || isActive ? color + '25' : '#F5F3EF',
                      color: isDone || isActive ? color : '#C9B8A8',
                      border: isActive ? `1.5px solid ${color}` : '1px solid transparent',
                    }}
                  >
                    {STAGE_LABELS[s]}
                  </button>
                  {i < STAGES.length - 1 && <ChevronRight size={12} style={{ color: '#E8E3D8' }} />}
                </React.Fragment>
              );
            })}
            {canEdit && (
              <button
                data-testid="edit-stage-btn"
                onClick={() => setEditStage(!editStage)}
                className="ml-2 p-1.5 rounded-lg transition-colors"
                style={{ color: editStage ? '#B87B7B' : '#C9B8A8' }}
              >
                {editStage ? <X size={16} /> : <Edit2 size={16} />}
              </button>
            )}
          </div>
        </div>

        {/* Client info */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 pt-6 border-t" style={{ borderColor: '#E8E3D8' }}>
          {project.client_email && <div><p className="text-xs tracking-wide" style={{ color: '#C9B8A8' }}>EMAIL</p><p className="text-sm mt-0.5" style={{ color: '#2D2D2D' }}>{project.client_email}</p></div>}
          {project.client_phone && <div><p className="text-xs tracking-wide" style={{ color: '#C9B8A8' }}>TELÉFONO</p><p className="text-sm mt-0.5" style={{ color: '#2D2D2D' }}>{project.client_phone}</p></div>}
          {project.description && <div className="sm:col-span-3"><p className="text-xs tracking-wide" style={{ color: '#C9B8A8' }}>DESCRIPCIÓN</p><p className="text-sm mt-0.5 font-light" style={{ color: '#2D2D2D' }}>{project.description}</p></div>}
        </div>
      </div>

      {/* Mobiliario a la medida */}
      <FurnitureList projectId={id} />

      {/* Tasks */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>Tareas del Proyecto</h2>
          <button
            data-testid="add-task-btn"
            onClick={() => setShowTaskModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-light tracking-wide transition-all duration-300"
            style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#C9A66B'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2D2D2D'}
          >
            <Plus size={14} /> Tarea
          </button>
        </div>

        {tasks.length === 0 ? (
          <div className="rounded-xl border p-10 text-center" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
            <p className="text-sm font-light" style={{ color: '#C9B8A8' }}>No hay tareas aún para este proyecto</p>
          </div>
        ) : (
          <div className="rounded-xl border overflow-hidden" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }}>
            {tasks.map((t, i) => (
              <div key={t.id} data-testid={`task-row-${t.id}`} className={`flex items-center gap-4 px-5 py-3.5 ${i > 0 ? 'border-t' : ''}`} style={{ borderColor: '#E8E3D8' }}>
                <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: t.priority === 'high' ? '#B87B7B' : t.priority === 'medium' ? '#D4A574' : '#8B9F8D' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-light truncate" style={{ color: '#2D2D2D' }}>{t.title}</p>
                  {t.category && <p className="text-[10px] mt-0.5" style={{ color: '#C9B8A8' }}>{t.category}</p>}
                </div>
                <select
                  data-testid={`task-status-${t.id}`}
                  value={t.status}
                  onChange={e => updateTaskStatus(t.id, e.target.value)}
                  className="text-xs rounded-lg px-2 py-1 outline-none cursor-pointer"
                  style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }}
                >
                  {TASK_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                </select>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Task Create Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={() => setShowTaskModal(false)}>
          <div className="rounded-2xl border p-8 w-full max-w-md shadow-xl" style={{ backgroundColor: '#FFFFFF', borderColor: '#E8E3D8' }} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-light" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#2D2D2D' }}>Nueva Tarea</h2>
              <button onClick={() => setShowTaskModal(false)} style={{ color: '#C9B8A8' }}><X size={20} /></button>
            </div>
            <form onSubmit={createTask} className="space-y-4">
              <div>
                <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>Título</label>
                <input data-testid="task-field-title" value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })} required
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }} placeholder="Título de la tarea" />
              </div>
              <div>
                <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>Descripción</label>
                <textarea data-testid="task-field-description" value={taskForm.description} onChange={e => setTaskForm({ ...taskForm, description: e.target.value })} rows={2}
                  className="w-full px-3 py-2.5 rounded-lg text-sm outline-none resize-none" style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }} placeholder="Opcional" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>Prioridad</label>
                  <select data-testid="task-field-priority" value={taskForm.priority} onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }}>
                    <option value="low">Baja</option>
                    <option value="medium">Media</option>
                    <option value="high">Alta</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium tracking-wider uppercase mb-1.5" style={{ color: '#C9B8A8' }}>Categoría</label>
                  <input data-testid="task-field-category" value={taskForm.category} onChange={e => setTaskForm({ ...taskForm, category: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={{ backgroundColor: '#FAF9F6', border: '1px solid #E8E3D8', color: '#2D2D2D' }} placeholder="Ej: Carpintería" />
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowTaskModal(false)} className="flex-1 py-2.5 rounded-lg text-sm border transition-colors" style={{ borderColor: '#E8E3D8', color: '#2D2D2D' }}>Cancelar</button>
                <button data-testid="task-submit-btn" type="submit" disabled={savingTask} className="flex-1 py-2.5 rounded-lg text-sm font-medium tracking-wide transition-all disabled:opacity-50" style={{ backgroundColor: '#2D2D2D', color: '#FAF9F6' }}>
                  {savingTask ? 'Creando...' : 'Crear Tarea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
